import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  twinService,
  type DerivedFact,
} from '../../services/twinService';
import { matchGroupKeys } from '../../hooks/useMatchGroups';
import type { GsSearchGroup } from './appSearchSources';

/**
 * #1392 — read the gutschrift-check match-group derived facts that are
 * already in the React-Query cache (all months, tenant-wide) and project
 * them to the flat ``GsSearchGroup`` search shape.
 *
 * This deliberately reuses the SAME query key
 * (``['matchGroups','derived-facts']``) the GS-Check page populates via
 * ``useMatchGroups`` — so app-search shares the cache, never refetches on
 * its account when the page already loaded it, and stays fully decoupled
 * from the canvas chokepoint files (MatchGraph / FilterBar /
 * DeliveryGroupFeed are untouched).
 */

export const GS_FACTS_KEY = [...matchGroupKeys.all, 'derived-facts'] as const;

interface RawFactValue {
  group_id?: unknown;
  period_ym?: unknown;
  status?: unknown;
  agreement_id?: unknown;
  agreement_ids?: unknown;
  expected_abrechnungs_numbers?: unknown;
  expected_settlement_pos_tokens?: unknown;
  money?: { delta?: unknown; currency?: unknown } | null;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

/** Pure projection: derived facts → searchable GS groups. */
export function factsToGsSearchGroups(
  facts: DerivedFact[] | null | undefined,
): GsSearchGroup[] {
  if (!Array.isArray(facts)) return [];
  const seen = new Set<string>();
  const out: GsSearchGroup[] = [];
  for (const fact of facts) {
    const fv = (fact?.fact_value ?? {}) as RawFactValue;
    const id = typeof fv.group_id === 'string' ? fv.group_id : '';
    const ym = typeof fv.period_ym === 'string' ? fv.period_ym : '';
    if (!id || !ym || seen.has(id)) continue;
    seen.add(id);

    const agreementNumbers = asStringArray(fv.agreement_ids);
    if (agreementNumbers.length === 0 && typeof fv.agreement_id === 'string') {
      agreementNumbers.push(fv.agreement_id);
    }
    const documentNumbers = [
      ...asStringArray(fv.expected_abrechnungs_numbers),
      ...asStringArray(fv.expected_settlement_pos_tokens),
    ];
    const money =
      fv.money &&
      typeof fv.money.delta === 'number' &&
      typeof fv.money.currency === 'string'
        ? { delta: fv.money.delta, currency: fv.money.currency }
        : null;

    out.push({
      id,
      ym,
      status: typeof fv.status === 'string' ? fv.status : 'unknown',
      // Product / partner names live on hydrated events+lines (separate
      // queries the page builds per month). The all-month fact carries
      // the structural + reference fields below, which are what app-
      // search matches against tenant-wide without an extra fetch.
      deliveryProductNames: [],
      settlementProductNames: [],
      agreementNumbers,
      partnerNames: [],
      documentNumbers,
      money,
    });
  }
  return out;
}

/**
 * Hook: tenant-wide GS search groups derived from the shared fact cache.
 *
 * ``activeFetch`` (true only on the GS-Check route) lets app-search warm
 * the cache when the user is already on the page; elsewhere it reads
 * whatever the cache holds (empty before the first GS-Check visit, in
 * which case app-search falls back to the /documents source).
 */
export function useGsSearchGroups(activeFetch: boolean): GsSearchGroup[] {
  const queryClient = useQueryClient();

  // Subscribe to the shared fact query so this hook re-renders when the
  // cache fills/updates. ``enabled`` mirrors the page: we never trigger
  // the fetch from a non-GS route (avoids a tenant-wide derived-facts
  // pull on every page just to power search).
  const factsQuery = useQuery({
    queryKey: GS_FACTS_KEY,
    queryFn: () =>
      twinService.queryDerivedFacts({
        fact_type: 'gutschrift_match_group',
        limit: 1000,
      }),
    enabled: activeFetch,
    staleTime: 60_000,
  });

  return useMemo(() => {
    // codex r4 P1 — when the caller has NOT enabled the fetch (unauthorized:
    // no apps.gutschrift_check, or dropdown closed off-route), surface NO GS
    // groups. A disabled React Query observer still exposes cached
    // ``GS_FACTS_KEY`` data via ``factsQuery.data``, and the explicit cache
    // read below would too — so feature-gated GS agreements/doc-refs left in
    // cache by a prior authorized visit (or pre-impersonation) must not leak
    // into Cmd/Ctrl+F. The cache is therefore only ever read for an authorized,
    // active caller.
    if (!activeFetch) return [];
    const fromQuery = factsQuery.data?.derived_facts;
    if (fromQuery) return factsToGsSearchGroups(fromQuery);
    // Active but the query hasn't resolved this mount — reuse any cache a
    // prior visit left behind so an authorized reopen is instant.
    const cached = queryClient.getQueryData<{ derived_facts: DerivedFact[] }>(
      GS_FACTS_KEY,
    );
    return factsToGsSearchGroups(cached?.derived_facts);
  }, [activeFetch, factsQuery.data, queryClient]);
}
